import { useDispatch, useSelector } from 'react-redux';
import { Modal, Radio, Table } from 'antd';
import {
  Button,
  CheckboxButton,
  CloseIcon,
  Datepicker,
  Dropdown,
  Feedback,
  Input,
  PlusIcon,
  RadioButton,
  Subtitle,
  TrashIcon,
} from '../../shared';
import { useState } from 'react';
import moment from 'moment';
import {
  parseImageUrlToFileItem,
  toMillisecond,
  toSecond,
  dozToPcs,
  pcsToDoz,
  getAverageSize,
  numToStr,
  getUnitOfSpecie,
} from '../../../lib/common.helpers';
import { addUtilData } from '../../../store/utils/utils.actions';
import TimeTrackInput, {
  toSecTimeTracks,
} from '../../shared/input/TimeTrackInput';
import {
  loadFarmsData,
  loadQueueData,
  loadSpatStorages,
  saveFarmSeedingForm,
} from '../../../store/farms/farms.actions';
import { sendSingleRequest } from '../../../apis';
import { showFeedback } from '../../../store/ui/ui.actions';
import { IFileWall, ITimeRange } from '../../../entities/general.entities';
import { IOysterLineResource } from '../../../entities/farms.entities';
import { TaggingManageForm } from '../../lines/LineTagButton';
import { loadTags } from '../../../store/automation/automation.actions';
import { renderCustomError } from '.';
import { loadInventories } from '../../../store/inventories/inventories.actions';
import { selectLang } from '../../../store/ui/ui.selector';
import { translate, transUnit } from '../../../lib/lang.helper';
import {
  selectProfile,
  selectAccount,
} from '../../../store/auth/auth.selector';
import {
  SelectInventoriesByFarmID,
  SelectIsBudgetVisible,
  SelectOysterSpatsByFarmID,
  SelectSeasonsByFarmID,
  SelectOysterFlupsySpatsByFarmID,
  SelectDetailedBudgetEnabled,
  SelectFarmsByFarmID,
  SelectSeedingFields,
} from '../../../store/extra/extra.selector';
import DirectionForm from '../../farm-util/DirectionForm';
import BatchSelection from '../../farm-util/BatchSelection';
import {
  extractSeedingForm,
  getSpatSourceLabel,
  inventoryLabel,
} from '../../../lib/farm.helpers';
import InstantOysterAssessment from './InstantOysterAssessment';
import { StageLabelSpan } from '../../CommonViews';
import MinMaxInputForm from './MinMaxInputForm';
import { IExpenseRequest } from '../../../entities/budget.entities';
import OperatorBudgetForm from '../OperatorBudgetForm';
import { initialBudgetForm } from '../../../lib/form.helpers';
import SeedSpatUpdateForm from './SeedSpatUpdateForm';
import ModalFeedbackView from '../../shared/feedback/ModalFeedbackView';
import FilesWall from '../../shared/pictures-wall/FilesWall';
import SelectBoat from '../../farmform/SelectBoat';
import { ICustomValue } from '../../../entities/automation.entities';
import CustomValuesForm from '../../farm-util/CustomValuesForm';
import TaxonomyGroupsForm from '../../farmform/TaxonomyGroupsForm';

interface IQuantity {
  id: number;
  quantity: number;
}

interface ISeedBag {
  start: number | null;
  end: number | null;
  quantity: number | null;
  spat_size: number | null;
}

interface IFormData {
  season_name: string;
  spat_storage_id: number | null;
  spat_amount_dz: number | null;
  spat_amount_pcs: number | null;
  planned_date_seed: number;
  planned_date_harvest: number;
  spat_size: number;
  spat_size_max: number | null;
  basket_count: number | null;
  spacing: number | null;
  density: number | null;
  comment: string | null;
  direction: string | null;
  boat_id?: number | null;
  time_tracks?: ITimeRange[];

  image_pdf_files?: IFileWall[];

  parent_id?: number | null;
  picked_inventories?: IQuantity[] | null;
  amount_unit?: 'pcs' | 'dz';
  stage: number | null;
  is_catch_spat?: boolean;

  start_position?: number | null;
  end_position?: number | null;

  cost?: number | null;
  expenses?: IExpenseRequest[];
  seed_bags?: ISeedBag[];
  custom_values?: ICustomValue[];

  assessment_data?: any;

  tagged_ids?: number[] | null;
  taxonomy_group_ids: number[];
}

const defaultForm: IFormData = {
  season_name: '',
  spat_storage_id: null,
  spat_amount_dz: null,
  spat_amount_pcs: null,
  planned_date_seed: moment().toDate().getTime(),
  planned_date_harvest: moment().toDate().getTime(),
  spat_size: 0,
  spat_size_max: null,
  basket_count: null,
  stage: 1,
  spacing: null,
  density: null,
  comment: null,
  start_position: null,
  end_position: null,
  direction: null,
  boat_id: null,
  image_pdf_files: [],
  parent_id: null,
  time_tracks: [],
  amount_unit: 'dz',
  taxonomy_group_ids: [],
};

interface Props {
  visible: boolean;
  title?: string;
  lineData: IOysterLineResource;
  data?: any;
  updateID?: number;
  onCancel: () => void;
  onConfirm: (data: any) => void;
  overConfirm?: (data: any) => Promise<any>;
  parentID?: number;
  onlyView?: boolean;
}

const OysterSeedModal = ({
  visible,
  title,
  lineData,
  data,
  updateID,
  onCancel,
  onConfirm,
  overConfirm,
  parentID,
  onlyView,
}: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;

  const oysterSpats = SelectOysterSpatsByFarmID(lineData.farm_id);
  const flupsySpats = SelectOysterFlupsySpatsByFarmID(lineData.farm_id);
  const spats = lineData.farm_is_flupsy ? flupsySpats : oysterSpats;
  const seasonData = SelectSeasonsByFarmID(lineData.farm_id).map(x => ({
    id: x.id.toString(),
    label: x.name,
    value: x.name,
  }));
  const inventories = SelectInventoriesByFarmID(lineData.farm_id);
  const showBudget = SelectIsBudgetVisible(lineData.farm_id);
  const detailedBudgetEnabled = SelectDetailedBudgetEnabled(lineData.farm_id);
  const farmsData = SelectFarmsByFarmID(lineData.farm_id);
  const customFields = SelectSeedingFields(lineData.farm_id);
  const defaultSavedForm = extractSeedingForm(
    farmsData.find(x => x.id === lineData.farm_id),
    data,
  );
  const curFarm = farmsData.find(x => x.id === lineData.farm_id);
  const curBoat = useSelector(selectProfile)?.current_boat;

  const [isSeasonNew, setIsSeasonNew] = useState<'old' | 'new'>('old');
  const [errors, setErrors] = useState<any>();
  const [formData, setFormData] = useState<IFormData>(() => {
    if (data) {
      let tmp: any = {};
      for (let k in defaultForm) {
        tmp[k] = data[k] ?? (defaultForm as any)[k];
        if (k === 'planned_date_seed' || k === 'planned_date_harvest') {
          tmp[k] = toMillisecond(tmp[k]);
        } else if (
          k === 'image_pdf_files' &&
          (tmp[k] === null || tmp[k] === undefined)
        ) {
          tmp[k] = [];
        }
      }
      if (data.spat_storage && !tmp.spat_storage_id) {
        tmp.spat_storage_id = data.spat_storage.id;
        tmp.stage = data.spat_storage.stage;
      }
      if (data.stage) {
        tmp.stage = data.stage;
      }
      if (data.seed_bags) {
        tmp.seed_bags = data.seed_bags.map((bag: ISeedBag) => ({
          ...bag,
          spat_size: bag.spat_size,
        }));
      }
      if (data.is_catch_spat) {
        tmp.is_catch_spat = data.is_catch_spat;
      }
      tmp.spat_size = data.spat_size as number;

      if (tmp.time_tracks && tmp.time_tracks.length > 0) {
        tmp.time_tracks = tmp.time_tracks.map((x: any) => ({
          start_time: toMillisecond(x.start_time),
          finish_time: toMillisecond(x.finish_time),
        }));
      }
      if (showBudget) {
        tmp = { ...tmp, ...initialBudgetForm(detailedBudgetEnabled, data) };
      }
      if (tmp.image_pdf_files) {
        tmp.image_pdf_files = tmp.image_pdf_files.map((x: any) =>
          typeof x === 'object' && x !== null ? x : parseImageUrlToFileItem(x),
        );
      }
      tmp.boat_id = data.boat_id ?? curBoat?.id;
      tmp.assessment_data = data.assessment_data ?? undefined;
      if (data.custom_values) {
        tmp.custom_values = data.custom_values;
      }
      tmp.tagged_ids = data.tagged_ids ?? null;
      tmp.taxonomy_group_ids =
        data.taxonomy_group_ids && data.taxonomy_group_ids.length > 0
          ? data.taxonomy_group_ids
          : (data.taxonomy_groups ?? []).map((x: { id: number }) => x.id);
      if (detailedBudgetEnabled && !tmp.expenses) {
        tmp.expenses = [
          { name: '', quantity: null, unit_price: null, amount: null },
        ];
      }
      return { ...defaultForm, ...defaultSavedForm, ...tmp };
    }
    return {
      ...defaultForm,
      ...defaultSavedForm,
      boat_id: curBoat?.id,
      assessment_data: undefined,
      expenses: detailedBudgetEnabled
        ? [{ name: '', quantity: null, unit_price: null, amount: null }]
        : undefined,
    };
  });
  const [selectedInventories, setSelectedInventories] = useState<number[]>(
    () => {
      if (data?.picked_inventories) {
        return data.picked_inventories.map((x: IQuantity) => x.id);
      }
      if (updateID) {
        return [];
      }
      if (inventories.some(x => x.id === lineData.inventory_id)) {
        const inv = inventories.find(x => x.id === lineData.inventory_id);
        return inv ? [inv.id] : [];
      }
      return [];
    },
  );
  const [floatsQuantities, setFloatsQuantities] = useState<
    Record<number, number | undefined>
  >(() => {
    if (data?.picked_inventories) {
      return data.picked_inventories.reduce(
        (p: Record<number, number>, c: IQuantity) => {
          p[c.id] = c.quantity;
          return p;
        },
        {} as Record<number, number>,
      );
    }
    return {};
  });
  const [errorMsg, setErrorMsg] = useState<string>();
  const [disabled, setDisabled] = useState(false);
  const [visibleSpatUpdate, setVisibleSpatUpdate] = useState(false);

  const selectedBatch = spats.find(x => x.id === formData.spat_storage_id);
  const availablePieces: number = selectedBatch
    ? selectedBatch.available_weight + (updateID ? data.spat_amount_pcs : 0)
    : -1;
  const availableDozens: number = selectedBatch
    ? pcsToDoz(selectedBatch.available_weight ?? 0) +
      (updateID ? data.spat_amount_dz : 0)
    : -1;

  const toggleDetailedAssess = (c: boolean) => {
    if (c) {
      setFormData(prv => ({
        ...prv,
        assessment_data: {
          size_min: prv.spat_size,
          size_max: prv.spat_size_max ?? prv.spat_size,
          size_avg: getAverageSize(prv.spat_size, prv.spat_size_max),
          estimated_dozens: prv.spat_amount_dz ?? 0,
          estimated_amount: prv.spat_amount_pcs ?? 0,
        },
      }));
    } else {
      setFormData(prv => ({
        ...prv,
        assessment_data: undefined,
      }));
    }
  };
  const toggleSeedBags = (c: boolean) => {
    if (c) {
      setFormData(prv => ({
        ...prv,
        seed_bags: [
          {
            start: null,
            end: null,
            quantity: null,
            spat_size: null,
          },
        ],
      }));
    } else {
      setFormData(prv => ({
        ...prv,
        seed_bags: undefined,
      }));
    }
  };
  const updateSeedBag = (i: number, key: string, value: string) => {
    let tmp = [...(formData.seed_bags ?? [])];
    const v = value.length <= 0 ? null : Number(value);
    (tmp[i] as any)[key] = v;
    setFormData({ ...formData, seed_bags: tmp });
  };
  const addSeedBag = () => {
    setFormData({
      ...formData,
      seed_bags: [
        ...(formData.seed_bags ?? []),
        {
          start: null,
          end: null,
          quantity: null,
          spat_size: null,
        },
      ],
    });
  };
  const deleteSeedBag = (i: number) => {
    const tmp = [...(formData.seed_bags ?? [])];
    tmp.splice(i, 1);
    setFormData({ ...formData, seed_bags: tmp });
  };
  const updateInventoryQuantity = (i: number, val: string) => {
    let tmp = { ...floatsQuantities };
    tmp[i] = val.length <= 0 ? undefined : Number(val);
    setFloatsQuantities(tmp);
  };
  const updateForm = (key: keyof IFormData, val: any) => {
    if (
      [
        'spat_amount_dz',
        'spat_amount_pcs',
        'basket_count',
        'cost',
        'spacing',
        'density',
        'spat_size',
        'spat_size_max',
        'start_position',
        'end_position',
      ].includes(key)
    ) {
      val = val.length <= 0 ? null : Number(val);
    } else if (['comment'].includes(key)) {
      val = val.length <= 0 ? null : val;
    }
    if (
      ['spat_storage_id', 'spat_amount_dz', 'spat_amount_pcs'].includes(key)
    ) {
      setErrors((prv: any) => ({
        ...(prv ?? {}),
        spat_storage_id: null,
        spat_amount: null,
      }));
    } else {
      setErrors((prv: any) => ({ ...(prv ?? {}), [key]: null }));
    }
    if (key === 'spat_storage_id') {
      const spat = spats.find(x => x.id === Number(val));
      let tmp = { ...formData, spat_storage_id: Number(val) };
      if (spat?.harvest) {
        if (spat.harvest.shell_length) {
          tmp.spat_size = spat.harvest.shell_length as number;
          if (tmp.assessment_data) {
            tmp.assessment_data.size_min = tmp.spat_size;
            tmp.assessment_data.size_max = tmp.spat_size;
            tmp.assessment_data.size_avg = tmp.spat_size;
          }
        }
        if (spat.harvest.shell_length_max) {
          tmp.spat_size_max = spat.harvest.shell_length_max;
          if (tmp.assessment_data) {
            tmp.assessment_data.size_max = tmp.spat_size_max;
            tmp.assessment_data.size_avg = getAverageSize(
              tmp.spat_size,
              tmp.spat_size_max,
            );
          }
        }
        if (spat.harvest.line_length) {
          tmp.basket_count = spat.harvest.line_length;
        }
        if (spat.harvest.amount) {
          tmp.spat_amount_pcs = spat.harvest.amount;
          tmp.spat_amount_dz = pcsToDoz(spat.harvest.amount);
        }
        if (spat.harvest.line_length && spat.harvest.amount) {
          tmp.density = Math.round(
            pcsToDoz(spat.harvest.amount) / spat.harvest.line_length,
          );
        }
      }
      if (spat) {
        tmp.stage = spat.stage;
      }
      setFormData(tmp);
    } else if (key === 'spat_size' || key === 'spat_size_max') {
      let tmp = { ...formData, [key]: val };
      if (tmp.spat_size !== null && tmp.assessment_data) {
        tmp.assessment_data.size_min = tmp.spat_size;
        tmp.assessment_data.size_max = tmp.spat_size_max ?? tmp.spat_size;
        tmp.assessment_data.size_avg = getAverageSize(
          tmp.spat_size,
          tmp.spat_size_max,
        );
      }
      setFormData(tmp);
    } else if (!val) {
      setFormData({ ...formData, [key]: val });
    } else if (key === 'spat_amount_dz') {
      let tmp = {
        ...formData,
        spat_amount_dz: val,
        spat_amount_pcs: dozToPcs(val),
      };
      if (tmp.basket_count) {
        tmp.density = Math.round((val / tmp.basket_count) * 10) / 10;
      }
      setFormData(tmp);
    } else if (key === 'spat_amount_pcs') {
      let tmp = {
        ...formData,
        spat_amount_pcs: val,
        spat_amount_dz: pcsToDoz(val),
      };
      if (tmp.basket_count) {
        tmp.density =
          Math.round((tmp.spat_amount_dz / tmp.basket_count) * 10) / 10;
      }
      setFormData(tmp);
    } else if (key === 'basket_count') {
      let tmp = { ...formData, basket_count: val };
      if (tmp.density && !tmp.spat_amount_dz) {
        if (tmp.amount_unit === 'pcs') {
          tmp.spat_amount_pcs = val * tmp.density;
          tmp.spat_amount_dz = pcsToDoz(val * tmp.density);
        } else {
          tmp.spat_amount_dz = val * tmp.density;
          tmp.spat_amount_pcs = dozToPcs(val * tmp.density);
        }
      } else if (tmp.spat_amount_dz && !tmp.density) {
        tmp.density = Math.round((tmp.spat_amount_dz / val) * 10) / 10;
      } else if (tmp.spat_amount_dz && tmp.density) {
        tmp.density = Math.round((tmp.spat_amount_dz / val) * 10) / 10;
      }
      setFormData(tmp);

      if (selectedInventories.length === 1) {
        let tmp = { ...floatsQuantities };
        tmp[selectedInventories[0]] = val;
        setFloatsQuantities(tmp);
      }
    } else if (key === 'density') {
      let tmp = { ...formData, density: val };
      if (tmp.basket_count) {
        if (tmp.amount_unit === 'pcs') {
          tmp.spat_amount_pcs = val * tmp.basket_count;
          tmp.spat_amount_dz = pcsToDoz(val * tmp.basket_count);
        } else {
          tmp.spat_amount_dz = val * tmp.basket_count;
          tmp.spat_amount_pcs = dozToPcs(val * tmp.basket_count);
        }
      } else if (tmp.spat_amount_dz) {
        tmp.basket_count = Math.ceil(tmp.spat_amount_dz / val);
      }
      setFormData(tmp);
    } else if (key === 'amount_unit') {
      let tmp = { ...formData, amount_unit: val };
      if (tmp.density && tmp.basket_count) {
        if (val === 'pcs') {
          tmp.spat_amount_pcs = tmp.density * tmp.basket_count;
          tmp.spat_amount_dz = pcsToDoz(tmp.density * tmp.basket_count);
        } else {
          tmp.spat_amount_dz = tmp.density * tmp.basket_count;
          tmp.spat_amount_pcs = dozToPcs(tmp.density * tmp.basket_count);
        }
      }
      setFormData(tmp);
    } else {
      setFormData({ ...formData, [key]: val });
    }
  };
  const assessFormIsValid = () => {
    if (!formData.assessment_data) return true;

    const assessData = formData.assessment_data;
    if (!assessData.size_min || !assessData.size_max || !assessData.size_avg) {
      setErrorMsg('Assessment size measuring can not be empty');
      return false;
    }
    if (Number(assessData.size_min) > Number(assessData.size_max)) {
      setErrorMsg('Assessment size measuring range is incorrect');
      return false;
    }
    return true;
  };
  const validFormData = () => {
    let tmpErr = { ...(errors ?? {}) };
    let res = true;
    if (!formData.season_name) {
      tmpErr.season_name = translate(lang, 'Add name');
      res = false;
    }
    if (Number(formData.basket_count) === 0) {
      tmpErr.basket_count = translate(
        lang,
        'Number of %s cannot be 0',
        lineData.farming_method,
      );
      res = false;
    }
    if (formData.planned_date_seed > formData.planned_date_harvest) {
      tmpErr.planned_date_seed = translate(lang, '_invalid_seed_date');
      res = false;
    }
    if (
      data?.is_catch_spat !== true &&
      (!formData.spat_storage_id ||
        !formData.spat_amount_dz ||
        formData.spat_amount_dz < 0)
    ) {
      tmpErr.spat_amount = translate(lang, '_sel_spat_amt');
      res = false;
    }
    if (
      data?.is_catch_spat !== true &&
      formData.spat_amount_dz &&
      formData.spat_amount_dz > availableDozens
    ) {
      setVisibleSpatUpdate(true);

      tmpErr.spat_amount = translate(
        lang,
        '_max_spat_amount',
        `${availableDozens} dozens (${availablePieces} pieces)`,
      );
      res = false;
    }
    if (
      formData.spat_size_max !== null &&
      formData.spat_size_max < formData.spat_size
    ) {
      tmpErr.spat_size = translate(lang, '_invalid_spat_size_range');
      res = false;
    }
    if (
      formData.time_tracks &&
      toSecTimeTracks(formData.time_tracks) === false
    ) {
      setErrorMsg('_invalid_time_range');
      res = false;
    }
    if (!updateID) {
      let containersCnt = 0;
      for (let ivtId of selectedInventories) {
        const ivt = inventories.find(x => x.id === ivtId);
        if (!ivt) {
          setErrorMsg('_inv_found_2');
          res = false;
          break;
        }
        const qty = floatsQuantities[ivtId] ?? undefined;
        if (isNaN(Number(qty)) || Number(qty) <= 0) {
          setErrorMsg(translate(lang, '_invalid_ivt_qty', ivt.name));
          res = false;
          break;
        }
        if (Number(qty) > ivt.available_quantity) {
          setErrorMsg(
            translate(
              lang,
              '_seed_floats_over',
              qty,
              ivt.name,
              ivt.available_quantity,
            ),
          );
          res = false;
          break;
        }
        containersCnt += Number(qty);
      }
      if (containersCnt !== Number(formData.basket_count)) {
        const cf = window.confirm(
          `You should put ${formData.basket_count} containers but you added ${containersCnt} containers. Are you sure?`,
        );
        if (!cf) return null;
      }
    }
    if (
      (formData.start_position === null ? 1 : 0) ^
      (formData.end_position === null ? 1 : 0)
    ) {
      window.alert(translate(lang, '_start_end_position'));
      return null;
    } else if (
      formData.start_position !== null &&
      formData.start_position !== undefined &&
      formData.start_position > (formData.end_position ?? 0)
    ) {
      window.alert(translate(lang, '_start_position_less_than'));
      return null;
    }
    if (formData.seed_bags && formData.seed_bags.length > 0) {
      for (let i = 0; i < formData.seed_bags.length; i++) {
        const bag = formData.seed_bags[i];
        if (bag.start === null || bag.end === null || bag.quantity === null) {
          window.alert(
            translate(
              lang,
              'Bags info of row %s: Start position, End position and Bags per cage are required',
              i + 1,
            ),
          );
          return null;
        }
        if (bag.start > bag.end) {
          window.alert(
            translate(
              lang,
              'Bags info of row %s: Start position must be equal or smaller than End position',
              i + 1,
            ),
          );
          return null;
        }
        if (bag.quantity <= 0) {
          window.alert(
            translate(
              lang,
              'Bags info of row %s: Bags per cage must be greater than 0',
              i + 1,
            ),
          );
          return null;
        }
      }
    }
    setErrors(tmpErr);
    if (!res) return null;

    if (!assessFormIsValid()) return null;

    let validData = {
      ...formData,
      line_id: lineData.id,
      planned_date_seed: toSecond(formData.planned_date_seed),
      planned_date_harvest: toSecond(formData.planned_date_harvest),
      expenses: formData.expenses?.filter(x => x.name && !!x.amount),
      custom_values: formData.custom_values?.filter(x => !!x.field_val),
      assessment_data: formData.assessment_data
        ? {
            ...formData.assessment_data,
            size_min: formData.assessment_data.size_min,
            size_max: formData.assessment_data.size_max,
            size_avg: formData.assessment_data.size_avg,
          }
        : undefined,
    };
    if (!!parentID) {
      validData.parent_id = parentID;
    } else if (!updateID) {
      const ms = lineData.growing_cycle?.main_seed;
      if (ms) {
        if (!window.confirm(translate(lang, '_line_seed_make_sure')))
          return null;
        validData.parent_id = ms.id;
      }
    }
    if (selectedInventories.length > 0) {
      validData.picked_inventories = selectedInventories.map(x => ({
        id: x,
        quantity: floatsQuantities[x] ?? 0,
      }));
    }
    if (formData.time_tracks) {
      const tt = toSecTimeTracks(formData.time_tracks);
      if (tt) {
        validData.time_tracks = tt;
      } else {
        setErrorMsg('_invalid_time_range');
        return null;
      }
    }
    if (!updateID && !validData.parent_id && lineData.growing_cycle) {
      if (!window.confirm(translate(lang, '_line_seed_make_sure'))) return null;
      validData.parent_id = lineData.growing_cycle.main_seed.id;
    }
    return validData;
  };
  const handleConfirmClick = async () => {
    if (onlyView) return;
    let validData = validFormData();
    if (!validData) return;

    setErrorMsg(undefined);
    setDisabled(true);
    if (!seasonData.some(x => x.value === validData?.season_name)) {
      await dispatch(addUtilData('season', validData.season_name));
    }
    if (!!overConfirm) {
      const ovc = await overConfirm(validData);
      if (ovc !== true) {
        setDisabled(false);
        if (ovc.errors) setErrors(ovc.errors);
        if (ovc.message) setErrorMsg(ovc.message);
      }
      return;
    }

    const res = updateID
      ? await sendSingleRequest(
          validData,
          'PUT',
          `api/farm/line/seeding/${updateID}`,
          true,
        )
      : await sendSingleRequest(
          validData,
          'POST',
          `api/farm/line/oyster-seeding`,
          true,
        );
    if (res.status) {
      if (validData.tagged_ids) {
        dispatch(loadTags());
      }
      saveFarmSeedingForm(dispatch, lineData.farm_id, {
        season_name: validData.season_name,
        spacing: validData.spacing,
        amount_unit: validData.amount_unit,
      });
      if (validData.assessment_data) {
        const assessmentForm = {
          ...validData.assessment_data,
          assessment_date: toSecond(validData.planned_date_seed),
          planned_date_harvest: toSecond(validData.planned_date_harvest),
          seeding_id: lineData.growing_cycle?.main_seed.id,
          line_id: lineData.id,
          custom_values: validData.assessment_data.custom_values?.filter(
            (x: any) => !!x.field_val,
          ),
        };
        const resAssessment = await sendSingleRequest(
          assessmentForm,
          'POST',
          'api/farm/line/assessment/assessments',
          true,
        );
        if (!resAssessment.status) {
          setDisabled(false);
          setErrorMsg(
            translate(lang, resAssessment.data?.message ?? 'Unknown error'),
          );
          dispatch(loadFarmsData());
          return;
        }
      }
      dispatch(loadFarmsData());
      dispatch(loadSpatStorages());
      dispatch(loadQueueData());
      dispatch(
        showFeedback({
          isMessage: true,
          message: translate(
            lang,
            res.data?.message ??
              (updateID ? '_updated_data_ok' : '_seed_data_submitted_ok'),
          ),
          type: 'success',
        }),
      );
      if (selectedInventories) {
        dispatch(loadInventories());
      }
      onConfirm(validData);
    } else {
      setDisabled(false);
      setErrorMsg(translate(lang, res.data?.message ?? 'Unknown error'));
      if (res.data?.errors) setErrors(res.data.errors);
    }
  };

  const bagsTable = [
    {
      key: 'start',
      title: translate(lang, 'Start position'),
      render: (_: any, x: any, i: number) => (
        <Input
          type='number'
          label=''
          onChange={e => updateSeedBag(i, 'start', e.target.value)}
          value={numToStr(x.start)}
        />
      ),
    },
    {
      key: 'end',
      title: translate(lang, 'End position'),
      render: (_: any, x: any, i: number) => (
        <Input
          type='number'
          label=''
          onChange={e => updateSeedBag(i, 'end', e.target.value)}
          value={numToStr(x.end)}
        />
      ),
    },
    {
      key: 'quantity',
      title: translate(lang, 'Bags per cage'),
      render: (_: any, x: any, i: number) => (
        <Input
          type='number'
          label=''
          onChange={e => updateSeedBag(i, 'quantity', e.target.value)}
          value={numToStr(x.quantity)}
        />
      ),
    },
    {
      key: 'spat_size',
      title: translate(lang, 'Avg shell size'),
      render: (_: any, x: any, i: number) => (
        <Input
          type='number'
          label=''
          onChange={e => updateSeedBag(i, 'spat_size', e.target.value)}
          value={numToStr(x.spat_size)}
          unit={getUnitOfSpecie(unitType, 'size_mm')}
        />
      ),
    },
    {
      key: 'actions',
      title: '',
      render: (__: any, _: any, i: number) => (
        <Button
          width='full'
          size={0}
          type='bordered'
          color='red'
          className='d-flex justify-center'
          onClick={() => deleteSeedBag(i)}
        >
          <TrashIcon color='#cc0101' />
        </Button>
      ),
    },
  ];

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
      width={detailedBudgetEnabled ? 800 : 600}
    >
      {visibleSpatUpdate && formData.spat_storage_id ? (
        <SeedSpatUpdateForm
          type='OYSTER'
          farmID={lineData.farm_id}
          spatStorageID={formData.spat_storage_id}
          spatAmount={
            (formData.amount_unit === 'dz'
              ? formData.spat_amount_dz
              : formData.spat_amount_pcs) ?? 0
          }
          unit={formData.amount_unit}
          onClose={() => {
            setVisibleSpatUpdate(false);
            setErrors(undefined);
          }}
        />
      ) : (
        <>
          <div className='wrap'>
            <div className='d-flex align-center mb-32'>
              <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
                {translate(lang, title ?? 'Oyster seed line')}
              </Subtitle>
            </div>
            <div>
              {!!errorMsg && (
                <Feedback
                  message={translate(lang, errorMsg)}
                  type={'error'}
                  theme='light'
                  position={10}
                  isGlobal
                  onClose={() => setErrorMsg(undefined)}
                />
              )}
              <TaxonomyGroupsForm
                className='mb-17 mt-17'
                value={formData.taxonomy_group_ids}
                onChange={ids => updateForm('taxonomy_group_ids', ids)}
                disabled={onlyView}
              />
              <Radio.Group
                className='d-flex mt-14 mb-17'
                onChange={e => setIsSeasonNew(e.target.value)}
                value={isSeasonNew}
              >
                <RadioButton
                  label={translate(lang, 'Existing season')}
                  value='old'
                />
                <RadioButton
                  className='ml-34'
                  label={translate(lang, 'New season')}
                  value='new'
                />
              </Radio.Group>
              <div
                className={
                  errors?.season_name ? 'invalid-form mb-16' : ' mb-16'
                }
              >
                {isSeasonNew === 'old' ? (
                  <Dropdown
                    label={translate(lang, 'Season name')}
                    placeholder={translate(lang, 'Choose Season')}
                    className='mr-16 w-100'
                    onChange={value => updateForm('season_name', value)}
                    options={seasonData}
                    value={formData.season_name}
                  />
                ) : (
                  <Input
                    label={translate(lang, 'New Season')}
                    placeholder={translate(lang, 'Season name')}
                    type='text'
                    value={formData.season_name}
                    onChange={e => updateForm('season_name', e.target.value)}
                  />
                )}
                {!!errors?.season_name && (
                  <div className='invalid-feedback'>{errors?.season_name}</div>
                )}
              </div>
              {!data?.is_catch_spat ? (
                <>
                  <div className='d-flex mb-16'>
                    <div className='w-100'>
                      {translate(lang, 'Measurement units')}
                    </div>
                    <div className='w-100'>
                      <Radio.Group
                        className='d-flex align-center'
                        onChange={e =>
                          updateForm('amount_unit', e.target.value)
                        }
                        value={formData.amount_unit}
                      >
                        <RadioButton label='dz' value='dz' />
                        <RadioButton
                          className='ml-17'
                          label='pcs'
                          value='pcs'
                        />
                      </Radio.Group>
                    </div>
                  </div>
                  <div
                    className={
                      errors?.spat_amount || errors?.spat_storage_id
                        ? 'invalid-form mt-17'
                        : ' mt-17'
                    }
                  >
                    <BatchSelection
                      className={`mb-${
                        formData.spat_storage_id === null ? '16' : '7'
                      }`}
                      batches={spats}
                      value={formData.spat_storage_id}
                      onChange={value => updateForm('spat_storage_id', value)}
                      disabled={onlyView}
                    />
                    {!!formData.spat_storage_id && (
                      <div className='mb-16'>
                        <Input
                          label={`${translate(lang, 'Amount')}${
                            formData.amount_unit === 'dz'
                              ? formData.spat_amount_pcs
                                ? ` ${formData.spat_amount_pcs} pieces`
                                : ''
                              : formData.spat_amount_dz
                                ? ` ${formData.spat_amount_dz} dozens`
                                : ''
                          }`}
                          placeholder={
                            availablePieces <= 0
                              ? translate(lang, 'Amount')
                              : translate(
                                  lang,
                                  '_max_spat_amount',
                                  formData.amount_unit === 'pcs'
                                    ? `${availablePieces} pieces (${availableDozens} dz)`
                                    : `${availableDozens} dz (${availablePieces} pieces)`,
                                )
                          }
                          type='number'
                          value={
                            formData.amount_unit === 'pcs'
                              ? numToStr(formData.spat_amount_pcs)
                              : numToStr(formData.spat_amount_dz)
                          }
                          max={
                            availableDozens > 0 ? availableDozens : undefined
                          }
                          onChange={e => {
                            formData.amount_unit === 'pcs'
                              ? updateForm('spat_amount_pcs', e.target.value)
                              : updateForm('spat_amount_dz', e.target.value);
                          }}
                          unit={formData.amount_unit === 'pcs' ? 'pcs' : 'dz'}
                        />
                        {formData.spat_amount_dz !== availableDozens && (
                          <div
                            className='color-blue-0 mt-4 ml-4 cursor-pointer'
                            onClick={() =>
                              updateForm(
                                'spat_amount_dz',
                                availableDozens.toString(),
                              )
                            }
                          >
                            {translate(
                              lang,
                              '_full_spats_seeded',
                              getSpatSourceLabel(
                                formData.spat_storage_id,
                                spats,
                              ),
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {!!errors?.spat_storage_id && (
                      <div className='invalid-feedback'>
                        {errors?.spat_storage_id}
                      </div>
                    )}
                    {!!errors?.spat_amount && (
                      <div className='invalid-feedback'>
                        {renderCustomError(errors?.spat_amount)}
                      </div>
                    )}
                    {!!selectedBatch && (
                      <div className='mb-16'>
                        <CheckboxButton
                          label={translate(
                            lang,
                            'Keep previous stage for this seeding',
                          )}
                          checked={formData.stage === selectedBatch.stage - 1}
                          disabled={onlyView || selectedBatch.stage <= 1}
                          onChange={e =>
                            updateForm(
                              'stage',
                              e.target.checked
                                ? selectedBatch.stage - 1
                                : selectedBatch.stage,
                            )
                          }
                        />
                      </div>
                    )}
                    <Dropdown
                      label={translate(lang, 'Batch Stage')}
                      options={[1, 2, 3, 4].map(x => ({
                        id: x.toString(),
                        value: x.toString(),
                        label: (
                          <StageLabelSpan stage={x} farmId={lineData.farm_id} />
                        ),
                      }))}
                      value={formData.stage?.toString()}
                      onChange={v => updateForm('stage', Number(v))}
                    />
                  </div>
                </>
              ) : (
                <div className='pt-8 pb-16 pl-4'>
                  <Radio.Group
                    value={formData.is_catch_spat}
                    onChange={e => updateForm('is_catch_spat', e.target.value)}
                  >
                    <Radio value={true}>
                      {translate(lang, '_line_is_catch_spat')}
                    </Radio>
                    <Radio value={false} className='mt-8'>
                      {translate(lang, '_line_was_catch_spat')}
                    </Radio>
                  </Radio.Group>
                </div>
              )}
            </div>
            <div
              className={`mb-24 mt-16 ${
                errors?.planned_date_seed ? 'invalid-form' : ''
              }`}
            >
              <Datepicker
                label={transUnit(lang, 'line_seed_date', curFarm?.structure)}
                defaultValue={formData.planned_date_seed}
                onChange={e =>
                  updateForm(
                    'planned_date_seed',
                    e?.toDate().getTime() ?? moment().toDate().getTime(),
                  )
                }
                required
              />
              {errors?.planned_date_seed && (
                <div className='invalid-feedback'>
                  {errors?.planned_date_seed}
                </div>
              )}
            </div>
            <div className='mb-16'>
              <Datepicker
                label={translate(lang, 'Planned harvest date')}
                defaultValue={formData.planned_date_harvest}
                onChange={e => {
                  if (
                    formData.planned_date_seed !== undefined &&
                    e &&
                    e!.toDate().getTime() < formData.planned_date_seed
                  ) {
                    updateForm(
                      'planned_date_harvest',
                      moment().toDate().getTime(),
                    );
                  } else {
                    updateForm(
                      'planned_date_harvest',
                      e?.toDate().getTime() ?? moment().toDate().getTime(),
                    );
                  }
                }}
                required
              />
            </div>
            <div className='d-flex justify-between mb-16'>
              <div
                className={`w-100 mr-3 ${
                  errors?.basket_count ? 'invalid-form' : ''
                }`}
              >
                <Input
                  label={translate(
                    lang,
                    'Number of %s',
                    lineData.farming_method,
                  )}
                  type='number'
                  value={numToStr(formData.basket_count)}
                  required
                  onChange={e =>
                    updateForm('basket_count', Number(e.target.value))
                  }
                />
                {errors?.basket_count && (
                  <div className='invalid-feedback'>{errors?.basket_count}</div>
                )}
              </div>
              <div className='w-100 ml-3'>
                <Input
                  label={translate(
                    lang,
                    '%s per %s',
                    formData.amount_unit == 'pcs' ? 'pieces' : 'dozens',
                    lineData.farming_method,
                  )}
                  type='number'
                  value={numToStr(formData.density)}
                  dataType='density'
                  onChange={e => updateForm('density', e.target.value)}
                  unit={formData.amount_unit}
                  required
                />
              </div>
            </div>
            <div className='mb-16'>
              <DirectionForm
                value={formData.direction}
                onChange={v => updateForm('direction', v)}
              />
            </div>
            <SelectBoat
              className='mb-24'
              value={formData.boat_id}
              onChange={v => updateForm('boat_id', v)}
            />
            {!!formData.time_tracks && (
              <TimeTrackInput
                data={formData.time_tracks}
                onChange={t => updateForm('time_tracks', t)}
                baseDate={formData.planned_date_seed}
                disabled={onlyView}
              />
            )}
            <MinMaxInputForm
              className='mt-12'
              label={translate(lang, 'Spat Size')}
              min={formData.spat_size}
              max={formData.spat_size_max}
              unit={getUnitOfSpecie(unitType, 'size_mm')}
              onMinChange={v => updateForm('spat_size', v)}
              onMaxChange={v => updateForm('spat_size_max', v)}
              errorMsg={errors?.spat_size}
            />
            <div className='mb-17'>
              <Input
                label={translate(lang, 'Basket spacing')}
                type='number'
                value={numToStr(formData.spacing)}
                dataType='spacing'
                unit={getUnitOfSpecie(unitType, 'size_mm')}
                required
                onChange={e => updateForm('spacing', e.target.value)}
              />
            </div>
            {!updateID && (
              <div className='pb-17'>
                <Dropdown
                  label={translate(lang, 'Select inventory')}
                  placeholder={translate(lang, 'Select inventory')}
                  mode='multiple'
                  options={inventories.map(x => ({
                    id: x.id.toString(),
                    value: x.id.toString(),
                    label: inventoryLabel(x),
                  }))}
                  value={selectedInventories.map(String) as any}
                  onChange={(values: any) =>
                    setSelectedInventories(values.map(Number))
                  }
                  disabled={onlyView}
                />
              </div>
            )}
            {selectedInventories.length > 0 && (
              <div className='mb-17'>
                {selectedInventories.map(ivtId => (
                  <Input
                    key={ivtId}
                    label={inventoryLabel(
                      inventories.find(t => t.id === ivtId),
                    )}
                    placeholder={translate(
                      lang,
                      'Maximum value is %s',
                      inventories.find(t => t.id === ivtId)?.available_quantity,
                    )}
                    type='number'
                    className='mb-15'
                    value={numToStr(floatsQuantities[ivtId])}
                    onChange={e =>
                      updateInventoryQuantity(ivtId, e.target.value)
                    }
                    max={
                      inventories.find(t => t.id === ivtId)?.available_quantity
                    }
                    disabled={onlyView}
                  />
                ))}
              </div>
            )}
            {!!showBudget && (
              <OperatorBudgetForm
                detailedBudgetEnabled={detailedBudgetEnabled}
                form={formData}
                onChange={setFormData}
                disabled={onlyView}
                showUnits={true}
              />
            )}
            <CustomValuesForm
              fields={customFields}
              values={formData.custom_values ?? []}
              onChange={d => setFormData(prv => ({ ...prv, custom_values: d }))}
              disabled={onlyView}
            />
            <div className='mt-17 mb-17'>
              <Input
                label={translate(lang, 'Comment')}
                placeholder={translate(lang, 'Comment')}
                type='textarea'
                value={formData.comment ?? ''}
                onChange={e => updateForm('comment', e.target.value)}
              />
            </div>
            <div className='d-flex mb-17'>
              <div className='w-100 mr-12'>
                <Input
                  label={translate(lang, 'Start position')}
                  type='number'
                  value={numToStr(formData.start_position)}
                  max={lineData.basket_count}
                  onChange={e => updateForm('start_position', e.target.value)}
                />
              </div>
              <div className='w-100 ml-12'>
                <Input
                  label={translate(lang, 'End position')}
                  type='number'
                  value={numToStr(formData.end_position)}
                  max={lineData.basket_count}
                  onChange={e => updateForm('end_position', e.target.value)}
                />
              </div>
            </div>
            <div className='mb-17'>
              <CheckboxButton
                label={translate(
                  lang,
                  'I have bags information for each cages',
                )}
                checked={!!formData.seed_bags}
                onChange={e => toggleSeedBags(e.target.checked)}
              />
            </div>
            {!!formData.seed_bags && formData.seed_bags.length > 0 && (
              <div className='mb-24'>
                <Table
                  rowKey={(record, i) => `seed-bag-${i}`}
                  className='table table--units table--small'
                  dataSource={formData.seed_bags}
                  columns={bagsTable}
                  pagination={false}
                />
                <div className='item-row'>
                  <div className='item-cell'>
                    <Button
                      width='full'
                      size={5}
                      type='bordered'
                      color='blue'
                      className='d-flex justify-center'
                      onClick={addSeedBag}
                    >
                      <PlusIcon />
                    </Button>
                  </div>
                  <div className='item-cell'></div>
                  <div className='item-cell'></div>
                  <div className='item-cell'></div>
                  <div className='item-cell-half'></div>
                </div>
              </div>
            )}
            <div className='mb-17'>
              <FilesWall
                label={translate(lang, 'PDF/Images')}
                fileList={formData.image_pdf_files}
                onChange={v => updateForm('image_pdf_files', v)}
              />
            </div>
            <div className='mb-17'>
              <CheckboxButton
                label={translate(lang, '_create_detailed_assess')}
                checked={!!formData.assessment_data}
                onChange={e => toggleDetailedAssess(e.target.checked)}
              />
            </div>
            {!!formData.assessment_data && (
              <InstantOysterAssessment
                lineData={lineData}
                formData={formData.assessment_data}
                onChange={data =>
                  setFormData(prv => ({ ...prv, assessment_data: data }))
                }
                disabled={onlyView || disabled}
              />
            )}
          </div>
          {!updateID && !visibleSpatUpdate && (
            <TaggingManageForm
              className='mb-17 mt-17'
              lineData={lineData}
              tagIDs={formData.tagged_ids}
              onChange={ids => updateForm('tagged_ids', ids)}
            />
          )}
          <div className='modal-button d-flex justify-end align-center'>
            <Button
              width={'small'}
              size={2}
              type='fill'
              color='red'
              className='rsp-btn ml-16'
              onClick={onCancel}
              disabled={disabled}
            >
              {translate(lang, 'Close')}
            </Button>
            <Button
              width={'small'}
              size={2}
              type='fill'
              color='green'
              className='rsp-btn ml-16'
              onClick={handleConfirmClick}
              disabled={disabled || onlyView}
            >
              {translate(lang, 'Confirm')}
            </Button>
          </div>
        </>
      )}
      <ModalFeedbackView />
    </Modal>
  );
};

export default OysterSeedModal;
